/**
 * Ayush · Anniversary Push worklist (1-Jun-2026)
 *
 * Built when Dipali flagged that 2026 launched late and Ayush needs to
 * push out anniversary offers for Jan→May in June.  This is a focused
 * worklist (not a Kanban) — Ayush scans down the table, ticks "Sent"
 * as he fires each WhatsApp/email, and the row falls off the list.
 *
 * Data source: GET /api/xiaomi/ayush/anniversary-push?months=...
 *
 * Doctrine:
 *   - One row per partner whose anniversary lands in a selected month.
 *   - The 4 v5-sheet columns Ayush curated are surfaced verbatim:
 *       dinner choices · weekend choice · holiday recommendation · his note.
 *   - "Mark sent" stamps the partner record so the row vanishes; admins
 *     can unmark if needed.
 *   - WhatsApp click-to-chat uses the primary phone + a tier-aware prefill.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, ChevronRight, CheckCircle2, Phone, MessageSquare, RefreshCw, Eye, Route as RouteIcon, Pencil } from 'lucide-react';
import OutcomeExportButton from '../components/OutcomeExportButton';
import BenefitOutcomePanel from '../components/BenefitOutcomePanel';
import MessageComposerModal from '../components/xiaomi/MessageComposerModal';
import EditablePref from '../components/xiaomi/EditablePref';
import AyushEditOptionsModal from '../components/xiaomi/AyushEditOptionsModal';
import api from '@/lib/api';
import { toast } from 'sonner';

const ALL_MONTHS = [
  { n: 1, label: 'Jan' }, { n: 2, label: 'Feb' }, { n: 3, label: 'Mar' },
  { n: 4, label: 'Apr' }, { n: 5, label: 'May' }, { n: 6, label: 'Jun' },
  { n: 7, label: 'Jul' }, { n: 8, label: 'Aug' }, { n: 9, label: 'Sep' },
  { n: 10, label: 'Oct' }, { n: 11, label: 'Nov' }, { n: 12, label: 'Dec' },
];
const TIER_COLOR = {
  'Platinum Luxe': { bg: '#FEF3C7', fg: '#92400E' },
  'Platinum Signature': { bg: '#DBEAFE', fg: '#1E3A8A' },
  'Platinum Prime': { bg: '#E0E7FF', fg: '#3730A3' },
  'Rising Platinum': { bg: '#F3F4F6', fg: '#374151' },
};

export default function XiaomiAyushPush() {
  const navigate = useNavigate();
  const todayMonth = new Date().getMonth() + 1;
  // Default = Jan→Jun (the launch backlog Dipali asked for)
  const [selectedMonths, setSelectedMonths] = useState([1, 2, 3, 4, 5, 6]);
  const [includeDone, setIncludeDone] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  // "My Tasks Today" — when on, filter rows to those whose anniversary is today.
  const [todayOnly, setTodayOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/xiaomi/ayush/anniversary-push?months=${selectedMonths.join(',')}&include_done=${includeDone}`,
      );
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load worklist');
    } finally {
      setLoading(false);
    }
  }, [selectedMonths, includeDone]);

  useEffect(() => { load(); }, [load]);

  const toggleMonth = (n) => {
    setSelectedMonths((prev) =>
      prev.includes(n) ? prev.filter((m) => m !== n) : [...prev, n].sort((a, b) => a - b),
    );
  };

  const markSent = async (row, monthYm) => {
    try {
      await api.post(`/xiaomi/ayush/anniversary-push/${row.retailer_id}/mark-sent`, {
        month_ym: monthYm,
        note: 'Marked sent from worklist',
      });
      toast.success(`${row.outlet_name} marked sent`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mark-sent failed');
    }
  };

  const addNote = async (row, text) => {
    const t = (text || '').trim();
    if (!t) return false;
    try {
      const monthYm = `${new Date().getFullYear()}-${String(row.anniversary_month).padStart(2, '0')}`;
      await api.post(`/xiaomi/ayush/anniversary-push/${row.retailer_id}/note`, {
        text: t, month_ym: monthYm,
      });
      toast.success('Note saved');
      load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save note');
      return false;
    }
  };

  // 02-Jun-2026 — Per Dipali's doctrine: NEVER fire WhatsApp direct.
  // EVERY message goes through Zoho. This builder produces the
  // pre-filled English source text that the MessageComposerModal will
  // translate + push as a Zoho comment (which Tanisha's team fires on WA).
  // Includes dinner / weekend / holiday suggestions Ayush curated in the
  // Excel sheet, plus a discreet reference to 2025's history so we don't
  // repeat last year's experience.
  const buildComposerPrefill = (row) => {
    const name = row.salutation
      ? `${row.salutation} ${row.owner_first_name || ''}`.trim()
      : (row.owner_first_name || 'Sir');
    const dinner = row.weekend_anniversary_dinner || row.dinner_4star || '';
    const weekend = row.weekend_getaway || '';
    const holiday = row.holiday_destination || '';
    const lastDinner = row.history_2025?.restaurant_visits;

    const lines = [];
    lines.push(`Hi ${name}, wishing you a very happy anniversary!`);
    lines.push(``);
    lines.push(`As part of your Xiaomi Platinum 2026 programme, we've curated a few options for you and ${row.spouse_first_name || 'your family'}:`);
    if (dinner) lines.push(`• Anniversary dinner: ${dinner}`);
    if (weekend) lines.push(`• Weekend getaway option: ${weekend}`);
    if (holiday) lines.push(`• Holiday option: ${holiday}`);
    lines.push(``);
    if (lastDinner && lastDinner !== 'No record') {
      lines.push(`(Last year we hosted you at ${lastDinner} — we'd love to try somewhere new this time.)`);
      lines.push(``);
    }
    lines.push(`Please share your preferred date / option and I'll get everything set up.`);
    lines.push(``);
    lines.push(`— Ayush, Les Concierges`);
    return lines.join('\n');
  };

  // Compose-via-Zoho modal state — store ID only so we always read the
  // freshest row from `data.rows` (which auto-syncs after any pref edit).
  const [composerRowId, setComposerRowId] = useState(null);
  const [editOptionsRowId, setEditOptionsRowId] = useState(null);

  const allRows = data?.rows || [];
  // Filter to today's anniversaries when "Tasks Today" is on. The
  // `anniversary` field is `DD MMM` (e.g. "02 Jun"); match by current
  // day-of-month + short month name.
  const rows = useMemo(() => {
    if (!todayOnly) return allRows;
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const shortMonth = now.toLocaleString('en-GB', { month: 'short' });
    const todayKey = `${day} ${shortMonth}`;
    return allRows.filter((r) => (r.anniversary || '').startsWith(todayKey));
  }, [allRows, todayOnly]);

  // Group by anniversary month for the kanban-style header
  const grouped = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const k = r.anniversary_month_label;
      (m[k] ||= []).push(r);
    });
    return m;
  }, [rows]);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-5" data-testid="ayush-push-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#FF6900', fontFamily: 'Fraunces, serif' }}>
            Ayush · Anniversary Push
          </h1>
          <p className="text-sm text-soft mt-1">
            Pre-curated dinner / weekend / holiday choices for every Platinum Partner whose
            anniversary lands in the selected months. Tick "Sent" as you fire each touch — the row drops off the list.
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border bg-white hover:bg-[#fef3c7] disabled:opacity-50"
          data-testid="ayush-push-refresh">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/xiaomi/ayush/anniversary-push/export.csv?months=${selectedMonths.join(',')}&include_done=true`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border bg-white hover:bg-[#fef3c7]"
          data-testid="ayush-push-export-csv"
        >
          ⇩ CSV
        </a>
        <OutcomeExportButton
          benefitId="xiaomi_b08_anniversary_staycation"
          label="Outcomes CSV (where + feedback)"
          defaultPreset="anniversary_status" />
        <label
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border bg-white hover:bg-[#fef3c7] cursor-pointer"
          data-testid="ayush-push-import-csv"
        >
          ⇧ Import CSV
          <input type="file" accept=".csv,text/csv" className="hidden"
            data-testid="ayush-push-import-file"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              const form = new FormData();
              form.append('file', f);
              try {
                const r = await api.post(
                  '/xiaomi/ayush/anniversary-push/import.csv', form,
                  { headers: { 'Content-Type': 'multipart/form-data' } });
                const d = r.data;
                toast.success(
                  `Imported · ${d.updated} updated · ${d.marked_sent} marked sent · `
                  + `${d.notes_added} notes · ${d.errors} error(s)`,
                  { duration: 6000 });
                if (d.errors > 0) {
                  /* eslint-disable no-console */
                  console.warn('[ayush import] errors:', d.samples?.errors);
                }
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Import failed');
              }
            }} />
        </label>
      </div>

      {/* Month chips + include-done toggle */}
      <div className="flex items-center gap-2 flex-wrap p-3 rounded-lg" style={{ backgroundColor: '#FFF8EE', border: '1px solid #FFD7A5' }}>
        <Calendar size={14} className="text-[#9A4B0E]" />
        <span className="text-xs font-semibold text-[#9A4B0E] mr-2">Months:</span>
        {ALL_MONTHS.map((m) => {
          const active = selectedMonths.includes(m.n);
          return (
            <button key={m.n} onClick={() => toggleMonth(m.n)}
              className="text-[11px] px-2 py-1 rounded-full border transition-colors"
              style={{
                backgroundColor: active ? '#FF6900' : 'white',
                color: active ? 'white' : '#9A4B0E',
                borderColor: active ? '#FF6900' : '#FFD7A5',
              }}
              data-testid={`ayush-push-month-${m.n}`}>
              {m.label}
              {m.n === todayMonth && <span className="ml-0.5">·</span>}
            </button>
          );
        })}
        <button onClick={() => setSelectedMonths([1, 2, 3, 4, 5, 6])}
          className="text-[10px] underline ml-2 text-[#9A4B0E]"
          data-testid="ayush-push-jan-jun">
          Jan→Jun backlog
        </button>
        <button onClick={() => setSelectedMonths([todayMonth])}
          className="text-[10px] underline text-[#9A4B0E]"
          data-testid="ayush-push-this-month">
          This month
        </button>
        <label className="ml-auto flex items-center gap-1 text-[11px] text-[#9A4B0E]">
          <input type="checkbox" checked={includeDone}
            onChange={(e) => setIncludeDone(e.target.checked)}
            data-testid="ayush-push-include-done" />
          Show already-pushed
        </label>
        <label
          className="flex items-center gap-1 text-[11px] font-semibold cursor-pointer px-2 py-1 rounded"
          style={{ color: todayOnly ? '#fff' : '#9A4B0E', background: todayOnly ? '#FF6900' : 'transparent', border: '1px solid #FFD7A5' }}
        >
          <input type="checkbox" checked={todayOnly}
            onChange={(e) => setTodayOnly(e.target.checked)}
            className="hidden"
            data-testid="ayush-push-today-only" />
          🎯 Tasks today only
        </label>
      </div>

      {/* KPI strip · hide 0-value tiles pre-launch per Ayush's request */}
      {data && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {data.counts.total_rows > 0 && (
            <KpiTile label="Pending" n={data.counts.total_rows} color="#FF6900" />
          )}
          {data.counts.already_pushed > 0 && (
            <KpiTile label="Pushed" n={data.counts.already_pushed} color="#0F766E" />
          )}
          {Object.entries(data.counts.by_month)
            .filter(([, n]) => n > 0)
            .map(([m, n]) => (
              <KpiTile key={m} label={m} n={n} color="#1B2A4A" small />
            ))}
          {data.counts.total_rows === 0 && data.counts.already_pushed === 0 && (
            <div className="col-span-full text-[12px] text-soft italic px-2 py-1">
              No active counts in this window · widen the month selection above.
            </div>
          )}
        </div>
      )}

      {/* Worklist · grouped by month */}
      {loading && !data ? (
        <div className="text-center py-12 text-soft" data-testid="ayush-push-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 rounded-lg" style={{ backgroundColor: '#E8F7F4', color: '#0F766E' }}>
          <CheckCircle2 size={32} className="mx-auto mb-2" />
          <p className="text-sm font-semibold">No pending anniversaries in this window 🎉</p>
          <p className="text-xs mt-1 opacity-80">Switch months above or untick "Show already-pushed" to see history.</p>
        </div>
      ) : (
        Object.entries(grouped).map(([month, monthRows]) => (
          <div key={month} className="space-y-2">
            <h2 className="text-sm font-bold tracking-wide text-[#9A4B0E] mt-4 mb-2 flex items-center gap-2">
              <span className="px-2 py-0.5 rounded-full text-white text-[10px]" style={{ backgroundColor: '#FF6900' }}>
                {month}
              </span>
              <span>{monthRows.length} partner{monthRows.length === 1 ? '' : 's'}</span>
            </h2>
            <div className="space-y-2">
              {monthRows.map((row) => (
                <AyushRow key={row.retailer_id} row={row}
                  expanded={!!expanded[row.retailer_id]}
                  onToggle={() => setExpanded({ ...expanded, [row.retailer_id]: !expanded[row.retailer_id] })}
                  onMarkSent={(monthYm) => markSent(row, monthYm)}
                  onAddNote={(text) => addNote(row, text)}
                  onOpenPartner={() => navigate(`/xiaomi/partners/${row.retailer_id}`)}
                  onCompose={() => setComposerRowId(row.retailer_id)}
                  onEditOptions={() => setEditOptionsRowId(row.retailer_id)}
                  onPrefSaved={() => load()} />
              ))}
            </div>
          </div>
        ))
      )}

      {/* Compose-via-Zoho modal · Doctrine: NEVER fire WhatsApp direct.
          Read row from data.rows by id so it auto-syncs after pref edits. */}
      {composerRowId && (() => {
        const composerRow = rows.find((r) => r.retailer_id === composerRowId);
        if (!composerRow) return null;
        return (
          <MessageComposerModal
            dealerId={composerRow.retailer_id}
            dealerLanguage={composerRow.language_pref}
            benefitName="Anniversary Push"
            initialSource={buildComposerPrefill(composerRow)}
            editablePrefs={[
              { fieldPath: 'preferences_2026.weekend_anniversary_dinner', label: '2026 · Anniv dinner',     value: composerRow.weekend_anniversary_dinner || '' },
              { fieldPath: 'preferences_2026.dinner_4star',               label: '2026 · Dinner choices',   value: composerRow.dinner_4star || '' },
              { fieldPath: 'preferences_2026.holiday_ooty_first',         label: '2026 · Holiday option',   value: composerRow.holiday_ooty_first || '' },
            ]}
            onPrefSaved={() => load()}
            rebuildSource={(prefValues) => {
              // prefValues is keyed by dotted fieldPath; map back to flat row keys.
              const overrides = {
                weekend_anniversary_dinner: prefValues['preferences_2026.weekend_anniversary_dinner'] ?? composerRow.weekend_anniversary_dinner,
                dinner_4star:               prefValues['preferences_2026.dinner_4star']               ?? composerRow.dinner_4star,
                holiday_ooty_first:         prefValues['preferences_2026.holiday_ooty_first']         ?? composerRow.holiday_ooty_first,
              };
              return buildComposerPrefill({ ...composerRow, ...overrides });
            }}
            onClose={() => setComposerRowId(null)}
            onSaved={() => {
              toast.success('Pushed to Zoho · LSM will fire WhatsApp from there');
              setComposerRowId(null);
            }}
          />
        );
      })()}

      {/* Edit-options modal · batch edit all 6 fields at once */}
      {editOptionsRowId && (() => {
        const editRow = rows.find((r) => r.retailer_id === editOptionsRowId);
        if (!editRow) return null;
        return (
          <AyushEditOptionsModal
            row={editRow}
            onClose={() => setEditOptionsRowId(null)}
            onSaved={() => load()}
          />
        );
      })()}
    </div>
  );
}


function KpiTile({ label, n, color, small }) {
  return (
    <div className="rounded-lg bg-white border px-3 py-2" style={{ borderColor: '#E5E1DC' }}>
      <div className="text-[10px] uppercase tracking-wider text-soft">{label}</div>
      <div className={`font-bold ${small ? 'text-lg' : 'text-2xl'}`} style={{ color, fontFamily: 'Fraunces, serif' }}>{n}</div>
    </div>
  );
}


function AyushRow({ row, expanded, onToggle, onMarkSent, onAddNote, onOpenPartner, onCompose, onEditOptions, onPrefSaved }) {
  const tcol = TIER_COLOR[row.tier] || { bg: '#F3F4F6', fg: '#374151' };
  const monthYm = `${new Date().getFullYear()}-${String(row.anniversary_month).padStart(2, '0')}`;
  const [noteText, setNoteText] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const notes = row.notes || [];

  // Profile completeness (Ayush's PRD): 6 critical fields → % bar.
  // owner_name · phone_primary · anniversary · spouse · DOB · either weekend
  // dinner OR dinner option curated (so the message can be composed).
  const completenessChecks = [
    !!row.owner_first_name,
    !!row.phone_primary,
    !!row.anniversary,
    !!row.spouse_first_name,
    !!row.owner_dob || !!row.history_restaurant_visits, // history acts as DOB-proxy if missing
    !!(row.weekend_anniversary_dinner || row.dinner_4star || row.holiday_ooty_first),
  ];
  const completeness = Math.round(
    (completenessChecks.filter(Boolean).length / completenessChecks.length) * 100,
  );
  const compColor = completeness >= 85 ? '#16a34a' : completeness >= 50 ? '#f59e0b' : '#dc2626';

  // Stops the whole-card click from firing when a button/link inside is clicked.
  const stop = (e) => e.stopPropagation();

  return (
    <div className="bg-white rounded-lg border overflow-hidden hover:shadow-md cursor-pointer transition-shadow" style={{ borderColor: row.is_pushed ? '#BAE7DC' : '#E5E1DC' }}
      onClick={onOpenPartner}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpenPartner(); }}
      data-testid={`ayush-row-${row.retailer_id}`}>
      {/* Compact row */}
      <div className="flex items-start gap-3 p-3">
        <button onClick={(e) => { stop(e); onToggle(); }} className="mt-1 text-soft hover:text-[#15110d]"
          data-testid={`ayush-row-toggle-${row.retailer_id}`}>
          <ChevronRight size={14} className="transition-transform" style={{ transform: expanded ? 'rotate(90deg)' : 'none' }} />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[11px] text-soft">{row.retailer_id}</span>
            <span className="font-semibold text-[#15110d] truncate" title={row.outlet_name}>{row.outlet_name}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: tcol.bg, color: tcol.fg }}>
              {row.tier}
            </span>
            <span className="text-[10px] text-soft">{row.anniversary} · {row.city}</span>
          </div>
          <div className="text-[12px] text-soft mt-1">
            <strong className="text-[#15110d]">{row.salutation} {row.owner_first_name}</strong>
            {row.spouse_name && <> with {row.spouse_first_name}</>}
            {' · '}lang {row.language_pref}
            {' · '}{row.phone_primary}
          </div>
          {/* Completeness bar — Ayush's "is this partner ready to message?" indicator */}
          <div className="flex items-center gap-2 mt-1.5" title={`Profile completeness: ${completeness}%`}>
            <div className="flex-1 max-w-[160px] h-1.5 bg-[#f3f4f6] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${completeness}%`, background: compColor }} />
            </div>
            <span className="text-[10px] font-semibold" style={{ color: compColor }}>
              {completeness}%
            </span>
          </div>
          {row.ayush_action_note && (
            <p className="text-[11px] mt-1.5 italic" style={{ color: '#9A4B0E' }}>
              ▸ {row.ayush_action_note.slice(0, 180)}{row.ayush_action_note.length > 180 ? '…' : ''}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0" onClick={stop}>
          {row.phone_primary && (
            <a href={`tel:${row.phone_primary}`} title="Call" className="p-2 rounded hover:bg-[#f3f4f6]"
              data-testid={`ayush-row-call-${row.retailer_id}`}>
              <Phone size={12} />
            </a>
          )}
          {/* 02-Jun-2026 — Per doctrine: NEVER fire WhatsApp direct.
              Compose → translate → push to Zoho · Tanisha's team fires WA. */}
          <button
            onClick={(e) => { stop(e); onCompose(); }}
            title="Compose & push to Zoho (Tanisha's team fires WhatsApp)"
            className="p-2 rounded hover:bg-[#dcf8e8] flex items-center gap-1"
            style={{ color: '#128c7e' }}
            data-testid={`ayush-row-compose-${row.retailer_id}`}>
            <MessageSquare size={12} />
            <span className="text-[10px] font-semibold">Compose</span>
          </button>
          <button onClick={(e) => { stop(e); onEditOptions(); }}
            title="Edit pre-curated options (dinner / weekend / holiday) — audit-logged"
            className="p-2 rounded hover:bg-[#fef3c7]" style={{ color: '#9A4B0E' }}
            data-testid={`ayush-row-edit-options-${row.retailer_id}`}>
            <Pencil size={12} />
          </button>
          <button onClick={(e) => { stop(e); onOpenPartner(); }} title="Open 360°"
            className="p-2 rounded hover:bg-[#fef3c7]" style={{ color: '#9A4B0E' }}
            data-testid={`ayush-row-view-${row.retailer_id}`}>
            <Eye size={12} />
          </button>
          <Link
            to={`/xiaomi/partners/${row.retailer_id}/journey/xiaomi_b08_anniversary_staycation/${monthYm}`}
            title="Open 15-step Anniversary Staycation journey"
            className="p-2 rounded hover:bg-[#dcfce7]" style={{ color: '#0F766E' }}
            data-testid={`ayush-row-journey-${row.retailer_id}`}>
            <RouteIcon size={12} />
          </Link>
          {!row.is_pushed ? (
            <button onClick={(e) => { stop(e); onMarkSent(monthYm); }}
              className="px-2.5 py-1 rounded text-[11px] text-white font-semibold ml-1"
              style={{ backgroundColor: '#FF6900' }}
              data-testid={`ayush-row-mark-sent-${row.retailer_id}`}>
              ✓ Mark sent
            </button>
          ) : (
            <span className="px-2.5 py-1 rounded text-[11px] font-semibold ml-1 flex items-center gap-1"
              style={{ backgroundColor: '#E8F7F4', color: '#0F766E' }}>
              <CheckCircle2 size={11} /> Sent
            </span>
          )}
        </div>
      </div>

      {/* Expanded detail · the full v5 playbook · all 6 fields inline-editable */}
      {expanded && (
        <div className="border-t bg-[#fffbf5] p-3 space-y-2 text-[12px]"
          onClick={stop}
          data-testid={`ayush-row-detail-${row.retailer_id}`}>
          <EditablePref
            dealerId={row.retailer_id}
            fieldPath="preferences_2026.dinner_4star"
            label="2026 · Dinner choices (4★)"
            value={row.dinner_4star}
            onSaved={onPrefSaved}
            testId={`ayush-pref-dinner-${row.retailer_id}`}
          />
          <EditablePref
            dealerId={row.retailer_id}
            fieldPath="preferences_2026.weekend_anniversary_dinner"
            label="2026 · Weekend / anniversary dinner"
            value={row.weekend_anniversary_dinner}
            onSaved={onPrefSaved}
            highlight
            testId={`ayush-pref-weekend-${row.retailer_id}`}
          />
          <EditablePref
            dealerId={row.retailer_id}
            fieldPath="preferences_2026.holiday_ooty_first"
            label="2026 · Holiday recommendation"
            value={row.holiday_ooty_first}
            onSaved={onPrefSaved}
            testId={`ayush-pref-holiday-${row.retailer_id}`}
          />
          <div className="pt-2 mt-2 border-t" style={{ borderColor: '#fde68a' }}>
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: '#3730A3' }}>
              2025 · history (so we don't repeat last year)
            </div>
            <div className="space-y-2">
              <EditablePref
                dealerId={row.retailer_id}
                fieldPath="history_2025.restaurant_visits"
                label="2025 · Restaurants visited"
                value={row.history_restaurant_visits}
                onSaved={onPrefSaved}
                testId={`ayush-hist-restaurants-${row.retailer_id}`}
              />
              <EditablePref
                dealerId={row.retailer_id}
                fieldPath="history_2025.weekend_getaway"
                label="2025 · Weekend getaway"
                value={row.history_weekend_getaway}
                onSaved={onPrefSaved}
                testId={`ayush-hist-weekend-${row.retailer_id}`}
              />
              <EditablePref
                dealerId={row.retailer_id}
                fieldPath="history_2025.holiday_destination"
                label="2025 · Holiday destination"
                value={row.history_holiday_destination}
                onSaved={onPrefSaved}
                testId={`ayush-hist-holiday-${row.retailer_id}`}
              />
            </div>
          </div>
          <PlaybookField label="Ayush's action note" value={row.ayush_action_note} highlight />
          <PlaybookField label="CC remarks" value={row.cc_remarks} />
          <PlaybookField label="Profile status (CC)" value={row.profile_status_cc} />
          <PlaybookField label="Sentiment / feedback" value={row.feedback_sentiment_v5} />
          <div className="text-[10px] text-soft pt-1">
            Engagement {row.engagement_score_2025 || '—'} · Utilisation {row.utilisation_rate_2025 || '—'}
          </div>

          {/* Notes log · free-text context (vendor said X, partner asked for Y) */}
          {/* Universal Benefit Outcome panel · capture status/option/venue/date/feedback + photos+videos */}
          <BenefitOutcomePanel
            retailerId={row.retailer_id}
            benefitId="xiaomi_b08_anniversary_staycation"
            monthYm={monthYm}
            compact
          />

          {/* Notes log — free-text context that doesn't fit the stage workflow */}
          <div className="pt-2 mt-2 border-t" style={{ borderColor: '#FDE68A' }}
            data-testid={`ayush-notes-section-${row.retailer_id}`}>
            <div className="text-[10px] uppercase tracking-wider text-[#9A4B0E] font-bold mb-1">
              Notes ({notes.length})
            </div>
            <div className="space-y-1 mb-2 max-h-36 overflow-y-auto">
              {notes.length === 0 ? (
                <div className="text-[11px] italic text-soft">No notes yet.</div>
              ) : notes.map((n, i) => (
                <div key={i}
                  className="bg-white border rounded p-1.5 text-[11px]"
                  style={{ borderColor: '#FDE68A' }}>
                  <div className="text-[10px] text-soft mb-0.5">
                    {n.by || '?'}{n.month_ym ? ` · ${n.month_ym}` : ''}
                    {n.ts ? ` · ${new Date(n.ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}` : ''}
                  </div>
                  <div className="text-[#15110d] whitespace-pre-wrap">{n.text}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-1.5">
              <input type="text" value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && noteText.trim() && !noteBusy) {
                    setNoteBusy(true);
                    const ok = await onAddNote(noteText);
                    if (ok) setNoteText('');
                    setNoteBusy(false);
                  }
                }}
                placeholder="Add a note (partner asked for Goa hotel instead…)"
                className="flex-1 px-2 py-1 rounded border text-[11px] focus:outline-none focus:border-[#FF6900]"
                style={{ borderColor: '#FDE68A' }}
                data-testid={`ayush-note-input-${row.retailer_id}`} />
              <button onClick={async () => {
                if (!noteText.trim() || noteBusy) return;
                setNoteBusy(true);
                const ok = await onAddNote(noteText);
                if (ok) setNoteText('');
                setNoteBusy(false);
              }}
                disabled={noteBusy || !noteText.trim()}
                className="px-2 py-1 rounded text-white text-[11px] font-semibold disabled:opacity-40"
                style={{ backgroundColor: '#FF6900' }}
                data-testid={`ayush-note-add-${row.retailer_id}`}>
                + Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function PlaybookField({ label, value, highlight }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider"
        style={{ color: highlight ? '#9A4B0E' : '#9CA3AF', fontWeight: highlight ? 700 : 500 }}>{label}</div>
      <div className="text-[12px] mt-0.5" style={{ color: '#15110d' }}>{value}</div>
    </div>
  );
}
